import fs from "fs/promises";
import type { GetStaticProps, InferGetStaticPropsType, NextPage } from "next";
import Head from "next/head";
import path from "path";

import ProductPage from "@/components/Product/ProductPage";
import { DOMAIN_MAP } from "@/features/i18n/i18n.config.mjs";
import { fetchServerTranslations } from "@/i18n.server";
import { getAllProductPaths } from "@/lib/api/service/products/helpers";

import type { ProductPageData } from "../api/service/products/[...langAndUri]";
import { getFullProductPage } from "../api/service/products/[...langAndUri]";

// type NextPath = {
//   params: {
//     product: string;
//   };
//   locale?: string;
// };

// const giftWrappingSlug = process.env.GIFT_WRAPPING_SLUG;

type PathMap = Record<
  string,
  Awaited<ReturnType<typeof getAllProductPaths>>[number]
>;

const FILENAME = "locale-paths.json";
const FOLDER = "data";

/**
 * Generate alternate URLs for the current product page using provided URI translations
 * @param translatedUris a map from language code to URI
 * @returns Record<string, string> of locale to full product URIs
 */
function getAlternateUrls(translatedUris: Record<string, string>) {
  try {
    // Create URLs for all available translations
    return Object.entries(translatedUris).reduce((acc, [locale, uri]) => {
      const domain = DOMAIN_MAP[locale as keyof typeof DOMAIN_MAP]?.domain;

      if (!domain) {
        console.warn(`Missing domain configuration for locale: ${locale}`);
        return acc;
      }

      if (!uri) {
        console.warn(`Missing URI for locale: ${locale}`);
        return acc;
      }

      return {
        ...acc,
        [locale]: `https://${domain}/product/${uri}`,
      };
    }, {} as Record<string, string>);
  } catch (error) {
    console.error("Error generating alternate URLs:", error);
    return {};
  }
}

const Product: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  product,
  alternates,
}) => {
  return (
    <>
      <Head>
        {alternates &&
          Object.entries(alternates).map(([key, value]) => {
            return (
              <link
                key={key}
                rel="alternate"
                href={value}
                hrefLang={key}
                lang={key}
              />
            );
          })}
      </Head>
      <ProductPage product={product} />
    </>
  );
};

export default Product;

export const getStaticProps: GetStaticProps<{
  product: ProductPageData;
  alternates: Record<string, string> | null;
}> = async (context) => {
  const locale = context?.locale ?? "en";
  try {
    const uri = context?.params?.product as string;

    let notFound = false;

    const product = await getFullProductPage(uri, locale);

    let alternates = null;
    try {
      const variant =
        product.data.variants?.variants[product.data.variants?.default];
      const pathMap = JSON.parse(
        await fs.readFile(path.join(process.cwd(), FOLDER, FILENAME), "utf-8")
      ) as PathMap;

      const translation = variant?.info?.details?.id
        ? pathMap[variant?.info?.details?.id]
        : null;

      const translations = translation?.lang ?? null;

      if (locale === "no") {
        delete translations?.no;
      } else {
        delete translations?.en;
      }

      alternates = translations ? getAlternateUrls(translations) : null;
    } catch (e) {
      console.error(
        "Could not get alternats from local file for product id",
        product.data.variants?.variants
      );
    }

    // const ffRevalidationUrl = process.env.FF_REVALIDATION_URL as string;
    // await axios.post(ffRevalidationUrl, {
    //   product: product,
    //   language: locale || "en",
    //   productUri: uri,
    // });

    if (!product) {
      notFound = true;
    }

    return {
      props: {
        ...(await fetchServerTranslations(locale)),
        product,
        alternates,
      },
      notFound,
    };
  } catch (error) {
    return {
      props: {
        ...(await fetchServerTranslations(locale)),
        product: null,
        alternates: null,
      },
      notFound: true,
    };
  }
};

export const getStaticPaths = async () => {
  const rawPaths = await getAllProductPaths();

  const pathMap = rawPaths.reduce((acc, curr) => {
    if (!(curr.id in acc)) {
      acc[curr.id] = curr;
    }
    return acc;
  }, {} as PathMap);

  await fs.mkdir(path.join(process.cwd(), FOLDER), { recursive: true });
  await fs.writeFile(
    path.join(process.cwd(), FOLDER, FILENAME),
    JSON.stringify(pathMap, null, 2),
    "utf-8"
  );

  const paths = rawPaths
    .map((p) => {
      const locales = Object.keys(p.lang);
      // lang: { en: 'lynild-large-throw', no: 'lynild-store-pledd' }

      return locales.map((locale) => {
        return {
          params: {
            product: p.lang[locale],
          },
          locale,
        };
      });
    })
    .flat();

  return {
    paths,
    fallback: "blocking",
  };
};
